/* eslint-disable camelcase */
import {
  Box,
  Button,
  Card,
  Heading,
  MessageBlock,
  Paragraph,
} from '@deckee/deck-hand';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  ERROR_MESSAGE_DETAILS_PAGE__500_ALERT,
  ERROR_MESSAGE_DETAILS_PAGE__WEATHER_WARNING_EXPIRED,
} from '../../../../configurations/error-messages';
import { telemetry } from '../../../../services/backend';
import { ROUTE_WEATHER_MOST_RECENT_POSITION } from '../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../services/route/route-navigation';
import { setWeather } from '../../../../services/store/modules/app-controls';
import { getWarningDetails } from '../../../../services/weather';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import ErrorMessage from '../../../shared/ErrorMessage/ErrorMessage';
import {
  getWeatherAlertIcon,
  ReactSvg,
  sendArrow,
} from '../../../shared/vector-images';
import CustomDescription from '../../../shared/_deprecated/components/CustomDescription/CustomDescription';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';

const CardFooter = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.lightgrey};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FooterLink = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 16px 0;
  cursor: pointer;
`;

const WeatherWarningDetailsPage = (props) => {
  const { dispatchSetWeather } = props;

  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const theme = useTheme();

  const [data, setData] = useState();
  const [invalid, setInvalid] = useState(false);
  const [errorDescription, setErrorDescription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customDescription, setCustomDescription] = useState([]);

  const sharingPath = `weather/warning/${id}`;

  useEffect(() => {
    dispatchSetWeather();
  }, [dispatchSetWeather]);

  useEffect(() => {
    let isMounted = true;

    const getData = async () => {
      setIsLoading(true);
      try {
        const remoteData = await getWarningDetails(id);
        telemetry(
          telemetry.DEBUG,
          `[WeatherWarningDetailsPage.getData] received data details: ${JSON.stringify(
            remoteData,
          )}`,
        );
        if (isMounted) {
          setData(remoteData);
        }
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[WeatherWarningDetailsPage.getData] Error retrieving ${JSON.stringify(
            err,
          )}`,
        );
        if (isMounted) {
          setErrorDescription({
            message: ERROR_MESSAGE_DETAILS_PAGE__500_ALERT,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    getData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!data) return;

    const currentTimestamp = DateTime.local().toMillis();
    const validToTimestamp = DateTime.fromISO(data?.valid_to).toMillis();
    const timeDiff = validToTimestamp - currentTimestamp;
    setInvalid(timeDiff <= 0);

    const descriptions = [];

    if (data?.description) {
      descriptions.push({
        type: 'paragraph',
        content: data.description,
      });
    }

    if (data?.custom_description) {
      data.custom_description.forEach((custom) => {
        descriptions.push(custom);
      });
    }

    if (data?.videos && data.videos.length > 0) {
      data.videos.forEach((link) =>
        descriptions.push({
          type: 'video',
          content: link.url,
        }),
      );
    }

    setCustomDescription(descriptions);
  }, [data]);

  const updatedDate = DateTime.fromISO(data?.updated_at).toRelative();
  const issuedDate = DateTime.fromISO(data?.created_at).toRelative();

  const handleBack = useCallback(() => {
    if (location.key) {
      history.goBack();
    } else {
      transitionToNextRoute(ROUTE_WEATHER_MOST_RECENT_POSITION);
    }
  }, [history, location.key]);

  const handleShare = useCallback(() => {
    history.push('/maps/share', {
      sharePath: sharingPath,
      shareText: 'Check out this weather warning on Deckee!',
      title: data?.title,
      description: data?.description,
      type: 'weatherWarning',
      id,
    });
  }, [data?.description, data?.title, history, id, sharingPath]);

  const handleCheckForecast = useCallback(() => {
    transitionToNextRoute(ROUTE_WEATHER_MOST_RECENT_POSITION);
  }, []);

  return (
    <BasicDrawerLayout
      heading="Weather warning"
      onBack={handleBack}
      onClose={null}
    >
      <LayoutBody>
        {isLoading && <LogoSpinner background={false} />}

        {errorDescription && (
          <ErrorMessage description={errorDescription?.message} />
        )}

        {!isLoading && !errorDescription && data && (
          <>
            {/* Expired warning banner */}
            {invalid && (
              <Box mb="2">
                <MessageBlock type="warning" style={{ borderRadius: '12px' }}>
                  {ERROR_MESSAGE_DETAILS_PAGE__WEATHER_WARNING_EXPIRED}
                </MessageBlock>
              </Box>
            )}

            {/* Warning details card */}
            <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
              <Card.Body pb="0">
                {/* Centered warning icon */}
                <Box display="flex" justifyContent="center" pt="2" pb="3">
                  <ReactSvg
                    toDisplay={getWeatherAlertIcon(data?.subtype || data?.type)}
                    options={{
                      fillColor: theme.colors.danger,
                    }}
                    width={62}
                    height={62}
                  />
                </Box>

                {/* Warning title */}
                <Heading level={5} textAlign="center" mb="2">
                  {data?.title}
                </Heading>

                {/* Updated date */}
                <Paragraph textAlign="center" mb="3">
                  {updatedDate
                    ? `Updated ${updatedDate}`
                    : `Created ${issuedDate}`}
                </Paragraph>

                {/* Warning description content */}
                {customDescription.length > 0 && (
                  <Box mb="3" color="black">
                    <CustomDescription description={customDescription} />
                  </Box>
                )}

                {/* Source */}
                {data?.source && (
                  <Paragraph color="darkergrey" mb="3">
                    Source: {data.source}
                  </Paragraph>
                )}
              </Card.Body>

              {/* Share footer */}
              <CardFooter>
                <FooterLink onClick={handleShare}>
                  <Box display="flex" alignItems="center">
                    <ReactSvg
                      toDisplay={sendArrow()}
                      options={{ fillColor: theme.colors.primary }}
                      width={20}
                      height={20}
                    />
                    <Box
                      ml="1"
                      fontSize="15px"
                      fontWeight="bold"
                      color="primary"
                    >
                      Share
                    </Box>
                  </Box>
                </FooterLink>
              </CardFooter>
            </Card>

            {/* Is it safe for you? card */}
            <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
              <Card.Heading>Is it safe for you?</Card.Heading>
              <Card.Body>
                <Paragraph mb="3">
                  Get a personalised forecast based on your watercraft and where
                  you intend to explore.
                </Paragraph>
                <Button wide onClick={handleCheckForecast}>
                  Check forecast
                </Button>
              </Card.Body>
            </Card>
          </>
        )}
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

WeatherWarningDetailsPage.propTypes = {
  dispatchSetWeather: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  dispatchSetWeather: () => dispatch(setWeather({})),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(WeatherWarningDetailsPage);
